import React, { FC } from 'react'
import classNames from 'classnames'
import { BitcoinIcon } from '@/atoms/Icons/BitcoinIcon'
import { ExternalLinkIcon } from '@/atoms/Icons/ExternalLinkIcon'
import { Image } from '@/atoms/Image'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { useTranslate } from '@/utils/translate/translate-client'
import { GiveawayPrizeItem } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayConfigData'
import {
  GEyebrowLG,
  GEyebrowMD,
  GEyebrowSM,
  GParagraphMD,
  GParagraphSM,
  GResponsiveText,
  GTitleLG,
  GTitleSM,
  GTitleXL,
  GTitleXS,
} from '../UI/Typography'

interface Props {
  prizes: GiveawayPrizeItem[]
}

const kokTripItinerary = [
  {
    image: 'https://images.angelstudios.com/image/upload/v1739569476/vacation-london-image-01_q42hgs.jpg',
    title: 'Days 1-3',
    label: 'London – Walking in Dickens’ Footsteps',
    info: [
      {
        imageUrl: 'https://images.angelstudios.com/image/upload/v1740772040/the-stables-london_cfjxla.jpg',
        title: 'Explore London',
        description:
          'Explore the historic sites that inspired Oliver Twist and A Tale of Two Cities in Camden Town, and Lincoln’s Inn.',
      },
      {
        imageUrl: 'https://images.angelstudios.com/image/upload/v1738950984/box-office-image-01_kkihz5.jpg',
        title: 'Historic Pub and West End Performance',
        description:
          'Dine at a historic pub frequented by Dickens and enjoy a West End performance with a $150 per ticket stipend (4x).',
      },
      {
        imageUrl: 'https://images.angelstudios.com/image/upload/v1738950984/historic-home-interior-image-01_ziz12c.jpg',
        title: 'Canterbury Cathedral or Dickens House Museum',
        description:
          'Visit Dickens’ final home in Rochester and choose between exploring Canterbury Cathedral or Broadstairs’ Dickens House Museum.',
      },
    ],
  },
  {
    image: 'https://images.angelstudios.com/image/upload/v1739569475/airplane-take-off-01_vqgqov.jpg',
    title: 'Day 4',
    label: 'Flight to Israel',
    info: [
      {
        imageUrl: null,
        title: null,
        description:
          'Depart from London and arrive in Tel Aviv, welcomed with VIP service and transport to Netanya for an overnight stay.',
      },
    ],
  },
  {
    image: 'https://images.angelstudios.com/image/upload/v1739569474/isreal-dome-image-02_pncbrj.jpg',
    title: 'Days 5-10',
    label: 'The Holy Land – Walking in the Footsteps of Jesus',
    info: [
      {
        imageUrl: 'https://images.angelstudios.com/image/upload/v1740772040/israel-trip-01_xbpvra.jpg',
        title: 'Experience the Bible',
        description: 'Visit Caesarea, Nazareth, and Tiberias, experiencing biblical history firsthand.',
      },
      {
        imageUrl: 'https://images.angelstudios.com/image/upload/v1740772040/israel-trip-02b_qq91l5.jpg',
        title: 'Experience Biblical Adventures',
        description:
          'Participate in an archaeological dig in Chorazin, set sail on the Sea of Galilee, and kayak the Jordan River.',
      },
      {
        imageUrl: 'https://images.angelstudios.com/image/upload/v1740772040/israel-trip-03b_zukq3k.jpg',
        title: 'Journey Through Jerusalem’s Sacred Sites',
        description:
          'Walk the streets of Jerusalem, visit the Mount of Olives, Garden of Gethsemane, and Western Wall, concluding with communion at the Garden Tomb.',
      },
      {
        imageUrl: 'https://images.angelstudios.com/image/upload/v1740772040/israel-trip-04b_ypppyp.jpg',
        title: 'Discover the Wonders of the Dead Sea & Masada',
        description:
          'Explore the Dead Sea, Masada, and Ein Gedi, floating in the mineral-rich waters and trekking through breathtaking desert landscapes.',
      },
      {
        imageUrl: 'https://images.angelstudios.com/image/upload/v1740772040/israel-trip-05_d8i7ks.jpg',
        title: 'Leadership & Courage Seminar in the Valley of Elah',
        description:
          'End the journey with a leadership and courage seminar in the Valley of Elah, where David faced Goliath.',
      },
    ],
  },
]

export const TkokPrizeItemsTabContent: FC<Props> = ({ prizes }) => {
  const { t } = useTranslate('tickets')

  return (
    <div className="w-full px-4">
      <div className="my-10 md:mt-16">
        <GResponsiveText
          color="giveaway-text-primary-dark"
          MobileTextComponent={GTitleLG}
          DesktopTextComponent={GTitleXL}
        >
          {t('prizeItems', 'Prize Items')}
        </GResponsiveText>

        <GResponsiveText
          color="giveaway-text-subtle-light"
          className="mt-3"
          MobileTextComponent={GParagraphSM}
          DesktopTextComponent={GParagraphMD}
        >
          {t(
            'prizeItemsDescriptionLabelKok',
            'Explore the incredible prizes in this giveaway—an unforgettable journey through history and faith, plus the ultimate home theater experience!',
          )}
        </GResponsiveText>
      </div>

      <div className="mt-[48px] md:mt-[64px]">
        <PrizePackage
          title="Walk Through History – From Charles Dickens’ London to the Holy Land"
          price="$83,780"
          label={t('giveawayTrip', 'Trip')}
          description="Experience an unforgettable 10-day adventure that takes you from the literary streets of London to the sacred landscapes of Israel. Walk in the footsteps of Charles Dickens, exploring the world that shaped his greatest works. Then, journey to the Holy Land, where you’ll visit the very places where Jesus walked, taught, and performed miracles. This once-in-a-lifetime experience blends history, literature, and faith into an inspiring expedition you’ll never forget!"
          mobileImage="https://images.angelstudios.com/image/upload/v1739568889/tkok-trip-img-mob-01_b18clr.png"
          desktopImage="https://images.angelstudios.com/image/upload/v1739568889/tkok-trip-img-wide-01_vgl4rs.png"
        />

        <div className="flex w-fit items-center gap-2 rounded-full border-[2px] border-[#FFCA75] bg-[#FFEAC8] p-2 pr-3 md:pr-4">
          <BitcoinIcon />
          <GResponsiveText
            MobileTextComponent={GParagraphSM}
            DesktopTextComponent={GParagraphMD}
            color="giveaway-primary"
          >
            {t('bitcoinIncludedPrice', '$25,000 in BitCoin Included')}
          </GResponsiveText>
        </div>

        <div className="border-giveaway-accent-light my-10 border-b pb-1">
          <GTitleSM color="giveaway-primary">{t('giveawayTripItinerary', 'Trip Itinerary')}</GTitleSM>
        </div>

        <div className="grid gap-10">
          {kokTripItinerary.map(({ title, info, label, image }) => {
            return (
              <div key={title}>
                <div className="aspect-h-1 aspect-w-2 relative shrink-0 overflow-hidden rounded-none md:aspect-h-unset md:aspect-w-unset md:h-[230px] md:w-[408px]">
                  <Image src={image} alt="" fill />
                </div>

                <GTitleSM className="mt-4 block" color="giveaway-primary">
                  {title}
                </GTitleSM>
                <GParagraphMD className="mt-2 !font-bold" color="giveaway-primary">
                  {label}
                </GParagraphMD>

                <ul className="mt-2 grid gap-6 md:gap-4">
                  {info.map(({ imageUrl, title, description }, index) => {
                    if (!!imageUrl && !!title) {
                      return (
                        <li className="flex flex-col gap-4 md:flex-row md:items-center" key={`${description}-${index}`}>
                          <div className="aspect-h-1 aspect-w-[2.5] relative shrink-0 overflow-hidden rounded-none md:aspect-h-unset md:aspect-w-unset md:h-[140px] md:w-[140px]">
                            <Image src={imageUrl} alt="" fill className="object-cover" />
                          </div>
                          <div>
                            <GParagraphMD color="giveaway-text-subtle-light" className="inline !font-bold">
                              {title}
                            </GParagraphMD>
                            <ul className="marker:text-giveaway-subtle-light list-inside list-disc px-4">
                              <li>
                                <GParagraphMD color="giveaway-text-subtle-light" className="inline">
                                  {description}
                                </GParagraphMD>
                              </li>
                            </ul>
                          </div>
                        </li>
                      )
                    }
                    return (
                      <li key={`${description}-${index}`}>
                        <ul className="marker:text-giveaway-subtle-light list-inside list-disc px-4">
                          <li>
                            <GParagraphMD color="giveaway-text-subtle-light" className="inline">
                              {description}
                            </GParagraphMD>
                          </li>
                        </ul>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )
          })}
        </div>

        <PrizePackage
          className="mt-[48px] md:mt-[64px]"
          title="The Ultimate Home Theater Package"
          price="$16,212"
          label={t('giveawayPackage', 'Package')}
          description="Transform your living room into the ultimate movie-watching paradise! With this state-of-the-art home theater package, you'll enjoy an immersive cinematic experience without ever leaving home. Whether you're watching Homestead, The Shift, or the latest Hollywood blockbuster, this setup will make every viewing unforgettable."
          mobileImage="https://images.angelstudios.com/image/upload/v1739568890/tkok-theater-img-mob-01_k0bsq4.png"
          desktopImage="https://images.angelstudios.com/image/upload/v1739568891/tkok-theater-img-wide-01_ybupvz.png"
        />
      </div>
      <>
        <div className="border-giveaway-accent-light mb-5 mt-10 border-b pb-1">
          <GTitleSM color="giveaway-primary">{t('prizeItems', 'Prize Items')}</GTitleSM>
        </div>
      </>

      <div className="mb-10 grid gap-6 lg:grid-cols-2">
        {prizes
          .filter((prizeItem): prizeItem is GiveawayPrizeItem & { value: string } => !!prizeItem.value)
          .map((prizeItem, index) => (
            <PrizeItem
              key={index}
              image={prizeItem.image}
              alt={prizeItem.alt}
              title={prizeItem.title}
              value={prizeItem.value}
              url={prizeItem.url}
              sponsorName={prizeItem.sponsorName}
              representativePrizeShown={prizeItem.representativePrizeShown}
            />
          ))}
      </div>
    </div>
  )
}

interface PrizeItemProps {
  image: string
  alt: string
  title: string
  value: string
  url?: string
  sponsorName?: string
  representativePrizeShown?: boolean
}

const PrizeItem = ({ image, alt, title, value, url, sponsorName, representativePrizeShown }: PrizeItemProps) => {
  const { t } = useTranslate('tickets')
  const imageContent = (
    <div className="aspect-h-9 aspect-w-16 relative overflow-hidden rounded-none">
      <Image
        src={image}
        alt={alt}
        fill
        className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      />
      {representativePrizeShown && (
        <div className="absolute inset-0 ">
          <div className="relative h-full w-full">
            <Image
              src={'/v1747076634/repprizebig_ty4oa1.png'}
              alt={'Representitive Prize Shown'}
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          </div>
        </div>
      )}
      {url && sponsorName && (
        <GBFadeInExperiment experimentName="prize-sponsor-links" defaultValue={false}>
          <ExperimentVariation variation={true}>
            <div className="absolute inset-0 flex items-end justify-end p-[10px]">
              <div className="inline-flex items-center gap-1 rounded-none bg-white/90 px-2 py-1 text-xs font-medium text-giveaway-primary shadow-sm backdrop-blur-sm transition-all duration-200 group-hover:bg-white group-hover:shadow-md">
                <span>{t('visitSponsorName', 'Visit {{sponsorName}}', { sponsorName })}</span>
                <ExternalLinkIcon size={12} className="relative top-px" />
              </div>
            </div>
          </ExperimentVariation>
        </GBFadeInExperiment>
      )}
    </div>
  )

  return (
    <div className="group flex flex-col py-4">
      <GBFadeInExperiment experimentName="prize-sponsor-links" defaultValue={false}>
        <ExperimentVariation variation={true}>
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="relative overflow-hidden rounded-lg">
              {imageContent}
            </a>
          ) : (
            imageContent
          )}
        </ExperimentVariation>
        <ExperimentVariation variation={false}>{imageContent}</ExperimentVariation>
      </GBFadeInExperiment>
      <div className="flex justify-between gap-4 pb-1 pt-4">
        <div className="w-2/3">
          <GEyebrowSM color="giveaway-text-subtle-light">{t('item', 'item')}</GEyebrowSM>
          <GParagraphMD color="giveaway-primary">{title}</GParagraphMD>
        </div>
        <div>
          <GEyebrowSM color="giveaway-subtle">{t('value', 'value')}</GEyebrowSM>
          <GParagraphMD color="giveaway-primary">{value}</GParagraphMD>
        </div>
      </div>
    </div>
  )
}

interface PrizePackageProps {
  title: string
  price: string
  description: string
  mobileImage: string
  desktopImage: string
  label: string
  className?: string
}
const PrizePackage: FC<PrizePackageProps> = ({
  title,
  price,
  description,
  mobileImage,
  desktopImage,
  label,
  className,
}) => {
  const { t } = useTranslate('tickets')

  return (
    <div className={classNames('flex flex-col py-4', className)}>
      <div className="aspect-h-9 aspect-w-16 relative !min-h-[230px] overflow-hidden rounded-lg md:!aspect-h-1 md:!aspect-w-[2.387]">
        <Image alt="vacation" src={mobileImage} fill className="object-cover object-bottom md:hidden" />
        <Image alt="vacation" src={desktopImage} fill className="hidden object-contain object-bottom md:block" />
      </div>

      <div className="mt-6 flex flex-col-reverse justify-between gap-3 md:mt-4 md:flex-row md:gap-8">
        <div className="flex flex-col gap-1">
          <GResponsiveText
            color="giveaway-text-subtle-light"
            MobileTextComponent={GEyebrowMD}
            DesktopTextComponent={GEyebrowLG}
          >
            {label}
          </GResponsiveText>
          <GResponsiveText
            MobileTextComponent={GTitleXS}
            DesktopTextComponent={GTitleSM}
            color="giveaway-text-primary-light"
          >
            {title}
          </GResponsiveText>
        </div>

        <div className="flex shrink-0 flex-col gap-1">
          <GResponsiveText
            color="giveaway-text-subtle-light"
            MobileTextComponent={GEyebrowMD}
            DesktopTextComponent={GEyebrowLG}
          >
            {t('giveawayTotalValue', 'total value')}
          </GResponsiveText>
          <GResponsiveText
            MobileTextComponent={GTitleXS}
            DesktopTextComponent={GTitleSM}
            color="giveaway-text-primary-light"
          >
            {price}
          </GResponsiveText>
        </div>
      </div>

      {label === t('giveawayTrip', 'Trip') && (
        <div className="mt-4">
          <div className="flex w-full max-w-[394px] flex-col items-start gap-2 rounded-[10px] border border-[#D8D8D8] bg-white p-4">
            <p className="whitespace-nowrap font-roboto text-lg font-normal leading-7 text-[#6E4F46]">
              {t('broughtToYouByPremiumPartner', 'Brought to you by our premium partner')}
            </p>
            <div className="relative h-[99.94px] w-full max-w-[362px] self-stretch">
              <Image
                src="https://images.angelstudios.com/image/upload/f_auto,q_auto/v1739767787/king-of-kings/sarel-tours-logo-wide-01.png"
                alt="SAR-EL Tours & Conferences"
                fill
                className="object-contain"
              />
            </div>
          </div>
        </div>
      )}

      <GParagraphMD color="giveaway-text-subtle-light" className="mt-4">
        {description}
      </GParagraphMD>
    </div>
  )
}
